import { DoseCalcModeActive, PatientTherapySequenceStatus } from '../database/schema.js';
import { getDrugRoundingMode, getTotalAmountOfSubstanceDecimal, } from './calculations/patient-drug-total-amount.js';
/** Total amount a medicine's dose is recalculated to, rounded with its substance's rounding mode; null when it has no regular dose to recalculate. */
export function getRecalculatedTotalAmount(medicine, biometrics, roundingMode) {
    if (!medicine.regularDose)
        return null;
    return getTotalAmountOfSubstanceDecimal({
        calculationMethod: medicine.calculationMethod ?? DoseCalcModeActive.enum.ABSOLUTE,
        dose: medicine.regularDose,
        doseProportion: medicine.doseProportion ?? 100,
        roundingMode,
        dosePrecision: medicine.precisionDecimals ?? 2,
        height: biometrics.height ?? null,
        weight: biometrics.weight ?? null,
        gfr: biometrics.gfr ?? null,
        bsa: biometrics.bsa ?? null,
    });
}
/** The GFR used for doses: newest sample date, and the last entered one when several share that date. */
export function pickInUseGfr(gfrs) {
    return gfrs
        .filter(gfr => gfr.deletedAt === null)
        .toSorted((a, b) => b.labTime - a.labTime || (b.createdAt ?? 0) - (a.createdAt ?? 0))[0];
}
// Only prescriptions not yet validated by a doctor are recalculated (see medicineUpdateTotalAmounts);
// prescriptions awaiting board approval have not reached the doctor yet, so they follow the patient's values too.
export const RECALCULATED_STATUSES = [
    PatientTherapySequenceStatus.enum.pending_approval,
    PatientTherapySequenceStatus.enum.pending_doctor_validation,
];
// Future prescriptions validated by the doctor or further on keep their dose and must be checked manually (CY-379).
export const KEPT_DOSE_STATUSES = new Set([
    PatientTherapySequenceStatus.enum.validating,
    PatientTherapySequenceStatus.enum.production_planning,
    PatientTherapySequenceStatus.enum.in_production,
    PatientTherapySequenceStatus.enum.qa_dispatch,
    PatientTherapySequenceStatus.enum.released_for_delivery,
]);
function byDate(a, b) {
    return (a.therapyDate ?? 0) - (b.therapyDate ?? 0);
}
/**
 * AUC doses affected when the GFR in use changes to `newGfr`: `changed` are the recalculated prescriptions
 * whose amount differs, `notChanged` the future ones validated by the doctor or in production, which keep their amount.
 */
export function previewAucDoseChanges(medicines, biometrics, newGfr, now = Date.now()) {
    const startOfToday = new Date(now).setUTCHours(0, 0, 0, 0);
    const changed = [];
    const notChanged = [];
    for (const medicine of medicines) {
        if (medicine.calculationMethod !== DoseCalcModeActive.enum.AUC)
            continue;
        const status = medicine.therapySequence?.status;
        // a remake keeps the dose validated on the original, so the save leaves it out too (CY-303)
        if (medicine.therapySequence?.doseCopiedFromSequenceId)
            continue;
        if (status && RECALCULATED_STATUSES.includes(status)) {
            const newAmount = getRecalculatedTotalAmount(medicine, { ...biometrics, gfr: newGfr }, getDrugRoundingMode(medicine.activeSubstance));
            if (newAmount !== null && newAmount !== medicine.totalAmount) {
                changed.push({ medicine, oldAmount: medicine.totalAmount, newAmount });
            }
        }
        else if (status && KEPT_DOSE_STATUSES.has(status) && (medicine.therapyDate ?? 0) >= startOfToday) {
            notChanged.push(medicine);
        }
    }
    changed.sort((a, b) => byDate(a.medicine, b.medicine));
    notChanged.sort(byDate);
    return { changed, notChanged };
}
/**
 * Prescriptions affected when the patient's characteristics change from `oldBiometrics` to `newBiometrics`:
 * `recalculated` counts the prescriptions the save recalculates (as medicineUpdateTotalAmounts does), `kept` lists
 * the future validated ones whose dose depends on the changed values but keeps its amount.
 */
export function previewCharacteristicsDoseChanges(medicines, oldBiometrics, newBiometrics, now = Date.now()) {
    const startOfToday = new Date(now).setUTCHours(0, 0, 0, 0);
    const recalculated = new Set();
    const kept = [];
    for (const medicine of medicines) {
        const status = medicine.therapySequence?.status;
        // a remake keeps the dose validated on the original, so the save leaves it out too (CY-303)
        if (!status || medicine.therapySequence?.doseCopiedFromSequenceId)
            continue;
        const roundingMode = getDrugRoundingMode(medicine.activeSubstance);
        const newAmount = getRecalculatedTotalAmount(medicine, newBiometrics, roundingMode);
        if (newAmount === null)
            continue;
        if (RECALCULATED_STATUSES.includes(status)) {
            recalculated.add(medicine.therapySequenceId);
        }
        else if (KEPT_DOSE_STATUSES.has(status) &&
            (medicine.therapyDate ?? 0) >= startOfToday &&
            newAmount !== getRecalculatedTotalAmount(medicine, oldBiometrics, roundingMode)) {
            kept.push(medicine);
        }
    }
    kept.sort(byDate);
    return { recalculated: recalculated.size, kept };
}
//# sourceMappingURL=gfr-dose-changes.js.map